// Which account's local data (settings, lists, device files) this browser is showing.
// Each signed-in account gets its own storage keys, plus 'guest' for signed-out use, so
// switching accounts never shows – or uploads – another account's settings.

const PROFILE_KEY = 'kcl-canvas-profile';
export const GUEST = 'guest';
/** Data saved before profiles existed: owner unknown, so it is never adopted by an account. */
export const LEGACY = 'legacy';

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // storage blocked – profiles still switch in memory
  }
}

export const storageKey = (base: string, profile: string) => `${base}:${profile}`;

/**
 * Moves pre-profile data (`base` without a suffix) to the 'legacy' profile once. Returns
 * the profile to start with.
 */
export function initialProfile(bases: string[]): string {
  const stored = read(PROFILE_KEY);
  if (stored) return stored;
  let hadLegacy = false;
  for (const base of bases) {
    const legacy = read(base);
    if (legacy !== null) {
      if (read(storageKey(base, LEGACY)) === null) write(storageKey(base, LEGACY), legacy);
      write(base, null);
      hadLegacy = true;
    }
  }
  const start = hadLegacy ? LEGACY : GUEST;
  write(PROFILE_KEY, start);
  return start;
}

let active: string | null = null;

export function activeProfile(): string {
  return (active ??= initialProfile(['kcl-canvas-settings', 'kcl-canvas-lists']));
}

export function setActiveProfile(profile: string): void {
  active = profile;
  write(PROFILE_KEY, profile);
}

export function hasStored(base: string, profile: string): boolean {
  return read(storageKey(base, profile)) !== null;
}

/**
 * Calls `onChange` when another tab in this browser switches account. Tabs share the
 * session cookie, so a tab left showing the old account would otherwise keep its
 * settings on screen and save them into the new account.
 */
export function onProfileChangedElsewhere(onChange: () => void): () => void {
  const handler = (e: StorageEvent) => {
    if (e.key === PROFILE_KEY && e.newValue && e.newValue !== activeProfile()) onChange();
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}

export const profileFor =(userId: string | null | undefined): string => (userId ? `user-${userId}` : GUEST);
